// lib/module.ts
import { computed, reactive, readonly, toRaw, toRef, watch } from "vue";
function useModule(options) {
  const module = createModule(options);
  return flatten(module);
}
function createModule(options) {
  var _a;
  const module = {
    options
  };
  module.plugins = (_a = options.plugins) == null ? void 0 : _a.map((pluginInit) => {
    if (typeof pluginInit === "function") {
      return pluginInit(module);
    }
    return pluginInit;
  });
  return module;
}
function flatten(module) {
  var _a, _b, _c, _d;
  const flattenedModule = {};
  const { options, plugins } = module;
  const getInitialState = () => {
    var _a2;
    const { stateInit } = options;
    const source = (_a2 = stateInit == null ? void 0 : stateInit()) != null ? _a2 : {};
    if (!(plugins == null ? void 0 : plugins.length)) {
      return source;
    }
    return plugins.reduce((state2, plugin) => {
      if (plugin.onStateInit) {
        return plugin.onStateInit(state2);
      }
      return state2;
    }, source);
  };
  const registerOnDataChangeHooks = (state2) => {
    const onDataChangeHooks = plugins == null ? void 0 : plugins.filter((plugin) => plugin.onDataChange !== void 0).map((plugin) => plugin.onDataChange);
    if (onDataChangeHooks == null ? void 0 : onDataChangeHooks.length) {
      watch(state2, (value, oldValue, onCleanup) => {
        const rawState = toRaw(value);
        for (const dataChangeHook of onDataChangeHooks) {
          dataChangeHook == null ? void 0 : dataChangeHook(rawState, oldValue, onCleanup);
        }
      });
    }
  };
  const initialState = getInitialState();
  const state = reactive(initialState != null ? initialState : {});
  const refreshData = () => {
    const updatedState = getInitialState();
    Object.assign(state, updatedState != null ? updatedState : {});
  };
  flattenedModule.__metadata = {
    name: options.name,
    version: options.version
  };
  flattenedModule["refreshData"] = refreshData;
  for (const key in state) {
    flattenedModule[key] = readonly(toRef(state, key));
  }
  const getters = (_b = (_a = options.getters) == null ? void 0 : _a.call(options, state)) != null ? _b : {};
  for (const key in getters) {
    flattenedModule[key] = computed(getters[key]);
  }
  const mutations = (_d = (_c = options.mutations) == null ? void 0 : _c.call(options, state)) != null ? _d : {};
  for (const key in mutations) {
    flattenedModule[key] = mutations[key];
  }
  registerOnDataChangeHooks(state);
  return flattenedModule;
}

// lib/plugins.ts
var migrateState = (oldState, newState) => {
  const migratedState = {};
  for (const key in newState) {
    const [oldValue, newValue] = [oldState[key], newState[key]];
    if (key in oldState && typeof oldValue === typeof newValue) {
      migratedState[key] = oldValue;
    } else {
      migratedState[key] = newValue;
    }
  }
  return migratedState;
};
var LocalStoragePlugin = (module) => {
  const onDataChange = (value) => {
    const payload = {
      state: value,
      __version: module.options.version
    };
    localStorage.setItem(module.options.name, JSON.stringify(payload));
    return value;
  };
  return {
    onStateInit: (state) => {
      var _a, _b;
      const { name, version } = module.options;
      const stateString = localStorage.getItem(name);
      let initializedState;
      if (!stateString) {
        initializedState = state;
      } else {
        const payload = JSON.parse(stateString) || {};
        if (payload.__version !== version) {
          console.warn(
            `Upgrade module '${name}' from ${(_a = payload.__version) != null ? _a : 0} to ${version}.
States has been migrated`
          );
          initializedState = migrateState((_b = payload.state) != null ? _b : {}, state);
        } else {
          initializedState = payload.state;
        }
      }
      return onDataChange(initializedState);
    },
    onDataChange
  };
};
export {
  LocalStoragePlugin,
  createModule,
  flatten,
  useModule
};
